import Decimal from "decimal.js";
import type { Language } from "@/hooks/useLanguage";
import { TokenPrecisionUtils } from "@/lib/utils/token-precision";
import type { SponsoredHolding, SponsoredToken } from "./types";

export const RANK_META = [
	{
		label: "Champion",
		badge: "No.1",
		colors: ["#F7D774", "#D9A441"],
		glow: "rgba(247, 215, 116, 0.38)",
	},
	{
		label: "Runner-up",
		badge: "No.2",
		colors: ["#E8EDF5", "#9FA9BC"],
		glow: "rgba(232, 237, 245, 0.28)",
	},
	{
		label: "Third Place",
		badge: "No.3",
		colors: ["#D9A17A", "#9B6448"],
		glow: "rgba(217, 161, 122, 0.26)",
	},
] as const;

export function formatTokenPrice(token: SponsoredToken): string {
	return `${TokenPrecisionUtils.formatPriceDisplay(token.price, 3)} sats`;
}

function trimTrailingZeros(value: string): string {
	return value.replace(/\.?0+$/, "");
}

function formatCompactDecimal(
	value: Decimal,
	{
		maxDecimals = 2,
		withDollar = false,
	}: {
		maxDecimals?: number;
		withDollar?: boolean;
	} = {},
): string {
	if (!value.isFinite()) {
		return withDollar ? "$0" : "0";
	}

	const sign = value.isNegative() ? "-" : "";
	const absValue = value.abs();

	if (absValue.gte(1e9)) {
		return `${sign}${withDollar ? "$" : ""}${trimTrailingZeros(absValue.div(1e9).toFixed(maxDecimals))}B`;
	}

	if (absValue.gte(1e6)) {
		return `${sign}${withDollar ? "$" : ""}${trimTrailingZeros(absValue.div(1e6).toFixed(maxDecimals))}M`;
	}

	if (absValue.gte(1e3)) {
		return `${sign}${withDollar ? "$" : ""}${trimTrailingZeros(absValue.div(1e3).toFixed(maxDecimals))}K`;
	}

	if (withDollar) {
		return `${sign}$${absValue.toFixed(2)}`;
	}

	if (absValue.gte(100)) return `${sign}${absValue.toFixed(0)}`;
	if (absValue.gte(10)) return `${sign}${trimTrailingZeros(absValue.toFixed(1))}`;
	return `${sign}${trimTrailingZeros(absValue.toFixed(maxDecimals))}`;
}

export function getHoldingBalanceDecimal(holding: SponsoredHolding): Decimal {
	return new Decimal(TokenPrecisionUtils.balanceToDisplay(holding.balance, holding.token));
}

export function formatHoldingBalanceCompact(holding: SponsoredHolding): string {
	return formatCompactDecimal(getHoldingBalanceDecimal(holding));
}

export function formatHoldingBalanceFull(holding: SponsoredHolding): string {
	return TokenPrecisionUtils.formatBalanceDisplay(holding.balance, holding.token, 6);
}

export function calculateHoldingValueBtcDecimal(holding: SponsoredHolding): Decimal {
	return getHoldingBalanceDecimal(holding).mul(
		new Decimal(TokenPrecisionUtils.priceToBtc(holding.token.price)),
	);
}

export function calculateHoldingValueUsdDecimal(
	holding: SponsoredHolding,
	btcPrice: number,
): Decimal | null {
	if (!btcPrice || btcPrice <= 0 || !Number.isFinite(btcPrice)) return null;

	const usdValue = calculateHoldingValueBtcDecimal(holding).mul(btcPrice);
	if (!usdValue.isFinite()) return null;
	return usdValue;
}

export function formatHoldingUsdValue(
	holding: SponsoredHolding,
	btcPrice: number,
): string {
	const usdValue = calculateHoldingValueUsdDecimal(holding, btcPrice);
	if (!usdValue) return "$--";
	return formatCompactDecimal(usdValue, { withDollar: true });
}

export function formatHoldingUsdValueFull(
	holding: SponsoredHolding,
	btcPrice: number,
): string {
	const usdValue = calculateHoldingValueUsdDecimal(holding, btcPrice);
	if (!usdValue) return "$--";

	if (usdValue.gte(1)) {
		return usdValue.toNumber().toLocaleString("en-US", {
			style: "currency",
			currency: "USD",
			minimumFractionDigits: 2,
			maximumFractionDigits: 2,
		});
	}

	if (usdValue.gte(0.01)) return `$${usdValue.toFixed(4)}`;
	if (usdValue.gt(0)) return `$${usdValue.toFixed(6)}`;
	return "$0.00";
}

export function sanitizeDescription(text?: string): string {
	if (!text) return "Backed by the BabyOdin Foundation ranking board.";
	return normalizePosterText(text).replace(/\s+/g, " ").slice(0, 120);
}

export function getSponsoredDisplayCopy(token: SponsoredToken, language: Language): string {
	const customCopy =
		language === "zh"
			? normalizePosterText(token.customCopyZh)
			: normalizePosterText(token.customCopyEn || token.customCopyZh);

	if (customCopy) {
		return customCopy.replace(/\s+/g, " ").slice(0, 120);
	}

	return sanitizeDescription(token.description);
}

export function buildTokenImageUrl(tokenId: string): string {
	return `https://images.odin.fun/token/${tokenId}`;
}

export function formatSupportAgeLabel(
	supportAgeDays: number | undefined,
	language: Language,
): string {
	const normalizedDays =
		typeof supportAgeDays === "number" && Number.isFinite(supportAgeDays)
			? Math.max(1, Math.min(Math.floor(supportAgeDays), 10))
			: 10;

	if (language === "en") {
		return normalizedDays <= 1 ? "within 1 day" : `${normalizedDays}d ago`;
	}

	return normalizedDays <= 1 ? "1天内" : `${normalizedDays}天前`;
}

export function getSupportAgeToneClass(supportAgeDays: number | undefined): string {
	const normalizedDays =
		typeof supportAgeDays === "number" && Number.isFinite(supportAgeDays)
			? Math.max(1, Math.min(Math.floor(supportAgeDays), 10))
			: 10;

	if (normalizedDays >= 7) {
		return "text-red-500 dark:text-red-400";
	}

	if (normalizedDays >= 5) {
		return "text-amber-500 dark:text-amber-300";
	}

	return "text-gray-500 dark:text-gray-400";
}

export function svgToDataUri(svg: string): string {
	return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}

export function normalizePosterText(text?: string): string {
	if (!text) return "";

	// Normalize Unicode representation and drop control characters that can
	// destabilize SVG text shaping while preserving normal spaces/newlines.
	return text
		.normalize("NFC")
		.replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F-\u009F]/g, "")
		.trim();
}

export function truncatePosterText(text: string, limit: number): string {
	const normalized = normalizePosterText(text);
	const symbols = Array.from(normalized);

	if (symbols.length <= limit) {
		return normalized;
	}

	return `${symbols.slice(0, Math.max(limit - 1, 0)).join("").trimEnd()}…`;
}
